import type {
	AddonInput,
	BillingCycle,
	PlanTier,
	SubscriptionAddons,
	SubscriptionType,
} from "../types";

export const PLAN_NAME: Record<PlanTier, string> = {
	BUSINESS: "Business",
	ENTERPRISE: "Enterprise",
	AGENCY: "Agency",
};

export const SUBSCRIPTION_STATUS_LABEL: Record<SubscriptionType, string> = {
	TRIAL: "Trial",
	PAID: "Active",
	PAST_DUE: "Past due",
	CLOSED: "Closed",
};

export const SUBSCRIPTION_STATUS_COLOR: Record<
	SubscriptionType,
	"info" | "success" | "warning" | "default"
> = {
	TRIAL: "info",
	PAID: "success",
	PAST_DUE: "warning",
	CLOSED: "default",
};

export interface PlanOffer {
	maps: number;
	storageGb: number;
	viewers: number;
	isAddOnsAllowed: boolean;
	annualBaseUsd: number;
}

// Mirrors the v4 PLAN_OFFER from packages/ui-components/.../edit-subscription.
// Annual prices are pre-discount list prices (the partner discount of 30%
// applies on top per spec §5.1). Monthly is annualBaseUsd / 10 to give an
// effective ANNUAL "save 20%".
export const PLAN_OFFER: Record<PlanTier, PlanOffer> = {
	BUSINESS: {
		maps: 20,
		storageGb: 4,
		viewers: 0,
		isAddOnsAllowed: false,
		annualBaseUsd: 950,
	},
	ENTERPRISE: {
		maps: 40,
		storageGb: 8,
		viewers: 100,
		isAddOnsAllowed: true,
		annualBaseUsd: 1910,
	},
	AGENCY: {
		maps: 100,
		storageGb: 16,
		viewers: Infinity,
		isAddOnsAllowed: true,
		annualBaseUsd: 3380,
	},
};

export const BILLING_CYCLE_NAME: Record<BillingCycle, string> = {
	MONTHLY: "Monthly",
	ANNUAL: "Annual",
	TWO_YEARS: "2 years",
	THREE_YEARS: "3 years",
	FOUR_YEARS: "4 years",
	FIVE_YEARS: "5 years",
};

export const BILLING_CYCLE_DISCOUNT_PERCENT: Record<BillingCycle, number> = {
	MONTHLY: 0,
	ANNUAL: 20,
	TWO_YEARS: 30,
	THREE_YEARS: 30,
	FOUR_YEARS: 30,
	FIVE_YEARS: 30,
};

export const BILLING_CYCLE_DESCRIPTION: Record<BillingCycle, string> = {
	MONTHLY: "Per month",
	ANNUAL: "Per year",
	TWO_YEARS: "Per 2 years",
	THREE_YEARS: "Per 3 years",
	FOUR_YEARS: "Per 4 years",
	FIVE_YEARS: "Per 5 years",
};

export const ADDONS_ADMINS = [0, 1, 2, 3, 4, 5] as const;
export const ADDONS_EDITORS = [0, 5, 10, 15, 20, 25] as const;
export const ADDONS_MAPS = [0, 5, 10, 15, 20, 25] as const;
export const ADDONS_STORAGE = [0, 50, 100, 150, 200, 250] as const;

export type AddonAdmins = (typeof ADDONS_ADMINS)[number];
export type AddonEditors = (typeof ADDONS_EDITORS)[number];
export type AddonMaps = (typeof ADDONS_MAPS)[number];
export type AddonStorage = (typeof ADDONS_STORAGE)[number];

export const EMPTY_ADDONS: SubscriptionAddons = {
	admins: 0,
	editors: 0,
	maps: 0,
	storage: 0,
};

export interface SubscriptionEditorValues {
	cycle: BillingCycle;
	plan: PlanTier;
	addons: SubscriptionAddons;
}

export const formatUsd = (amount: number): string =>
	new Intl.NumberFormat("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount);

/**
 * Converts the keyed-counts form-shape add-ons object into the GraphQL
 * `AddonInput[]` wire shape expected by `previewManagedAccountSubscriptionChange`,
 * `activateManagedAccountToPaid`, and `updateManagedAccountSubscription`.
 *
 * Zero-quantity entries are omitted to keep the wire payload tight.
 */
export function addonsToInput(addons: SubscriptionAddons): AddonInput[] {
	const out: AddonInput[] = [];
	if (addons.admins > 0) out.push({ id: "admins", quantity: addons.admins });
	if (addons.editors > 0) out.push({ id: "editors", quantity: addons.editors });
	if (addons.maps > 0) out.push({ id: "maps", quantity: addons.maps });
	if (addons.storage > 0) out.push({ id: "storage", quantity: addons.storage });
	return out;
}
